import { CannedParams } from '../types/Content';
import { RequestParams, VerbalIdentity } from '../types/userSessionContext';
import {
  ContentType,
  GENERATE_CONTENT,
  GENERATE_CONTENT_ACTION,
  contentStyleOptions,
  UPLOAD_OPTION,
  toneLevelOptions,
} from '../constants/common';
import store from '../store/markettingContentGeneration';
import { filtersStateSegment } from '../store/globalStore/stateSegments/filtersStateSegment';
import { contentParamsSegment } from '../store/markettingContentGeneration/stateSegments/paramsSegment';
import { marketingEnhancementDefaults } from '../context/MarkettingPromptContext/constants';

export const getParamsUsed = (
  requestParams: RequestParams,
  updateParamsSegment: boolean = true
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
): any => {
  const paramsSegment = store.get(contentParamsSegment);
  const filtersState = store.get(filtersStateSegment);
  const paramsUsed = {
    action:
      requestParams.action.toLowerCase() === GENERATE_CONTENT.toLowerCase()
        ? GENERATE_CONTENT_ACTION
        : filtersState.actionsOptions.options.find(
            (option: CannedParams) =>
              option.name?.toLowerCase() === requestParams.action?.toLowerCase()
          ),
    brand: filtersState.brandsOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() === requestParams.brand?.toLowerCase()
    ),
    destinationFormat: filtersState.destinationFormatOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() === requestParams.channel?.toLowerCase()
    ),
    indication: filtersState.indicationsOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() === requestParams.indication?.toLowerCase()
    ),
    country: filtersState.countriesOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() === requestParams.country?.toLowerCase()
    ),
    audience: filtersState.audiencesOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() ===
        requestParams.targetAudience?.toLowerCase()
    ),
    contentType:
      requestParams.channel?.toLowerCase() !== ContentType.UPLOAD.toLowerCase()
        ? filtersState.channelsOptions.options.find(
            (option: CannedParams) =>
              option.name?.toLowerCase() ===
              requestParams.channel?.toLowerCase()
          )
        : UPLOAD_OPTION,
    persona: undefined,
    sections: [],
    tone: filtersState.tonesOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() === requestParams.tone?.toLowerCase()
    ),
    platform: filtersState.platformOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() === requestParams.platform?.toLowerCase()
    ),
    topics: (() => {
      const allTopicOptions = [
        ...filtersState.primaryMessagesOptions.options,
        ...(filtersState.primaryMessagesOptions.extendedLibraryItems || []),
      ];
      const topicKeys = requestParams?.topics || requestParams?.topic;

      return topicKeys
        ? allTopicOptions.filter((option: CannedParams) =>
            topicKeys.find(
              item =>
                item.toLowerCase() === (option.name?.toLowerCase() as string)
            )
          )
        : [];
    })(),
    contentStyle: contentStyleOptions.find((option: CannedParams) =>
      requestParams.contentStyle
        ? option.name?.toLowerCase() ===
          requestParams.contentStyle?.toLowerCase()
        : option.name?.toLowerCase() ===
          marketingEnhancementDefaults.currentSelectedContentStyle?.toLowerCase()
    ),
    toneLevel: toneLevelOptions.find((option: CannedParams) =>
      requestParams.toneLevel
        ? option.name?.toLowerCase() === requestParams.toneLevel?.toLowerCase()
        : option.name?.toLowerCase() ===
          marketingEnhancementDefaults.currentSelectedToneLevel?.toLowerCase()
    ),
    exclusions: (() => {
      if (!requestParams.exclusions) return '';
      return Array.isArray(requestParams.exclusions)
        ? requestParams.exclusions.join(', ')
        : requestParams.exclusions;
    })(),
    inclusions: (() => {
      if (!requestParams.inclusions) return '';
      return Array.isArray(requestParams.inclusions)
        ? requestParams.inclusions.join(', ')
        : requestParams.inclusions;
    })(),
    language: filtersState.languageOptions.options.find(
      (option: CannedParams) =>
        option.name?.toLowerCase() === requestParams.language?.toLowerCase()
    ),
    unbranded: requestParams.unbranded,
    context: requestParams.context?.text,
    daring:
      requestParams.verbalIdentity?.find(
        (item: VerbalIdentity) => item.name === 'DARING_AND_FOCUSED'
      )?.value || false,
    compassionate:
      requestParams.verbalIdentity?.find(
        (item: VerbalIdentity) => item.name === 'COMPASIONATE_AND_SPIRITED'
      )?.value || false,
    extendedLibraryEnabled: requestParams.extendedLibraryEnabled,
  };
  const updatedParamsSegment = {
    ...paramsSegment,
    currentSelectedBrand: paramsUsed?.brand?.name || '',
    currentSelectedAction: paramsUsed?.action?.name || '',
    currentSelectedContentType: paramsUsed?.contentType?.name || '',
    currentSelectedDestinationFormat: paramsUsed?.destinationFormat?.name || '',
    currentSelectedIndication: paramsUsed?.indication?.name || '',
    currentSelectedCountry: paramsUsed?.country?.name || '',
    currentSelectedAudience: paramsUsed?.audience?.name || '',
    currentSelectedChannel: paramsUsed?.contentType?.name || '',
    currentSelectedTone: paramsUsed.tone?.name || '',
    currentSelectedPlatform: paramsUsed?.platform?.name || '',
    currentSelectedContentStyle:
      paramsUsed?.contentStyle?.name ||
      marketingEnhancementDefaults.currentSelectedContentStyle,
    currentSelectedToneLevel:
      paramsUsed.toneLevel?.name ||
      marketingEnhancementDefaults.currentSelectedToneLevel,
    currentSelectedTopic:
      paramsUsed?.topics
        .map(topic => topic.name)
        .filter((name): name is string => typeof name === 'string') || [],
    currentSelectedLanguage: paramsUsed?.language?.name || '',
    currentSelectedExclusion: (() => {
      if (!requestParams.exclusions) return '';
      if (
        Array.isArray(requestParams.exclusions) &&
        requestParams.exclusions.length
      ) {
        return requestParams.exclusions.join(', ') || '';
      }
      return '';
    })(),
    currentSelectedInclusion: (() => {
      if (!requestParams.inclusions) return '';
      if (
        Array.isArray(requestParams.inclusions) &&
        requestParams.inclusions.length
      ) {
        return requestParams.inclusions.join(', ') || '';
      }
      return '';
    })(),
    currentSelectedContext: requestParams.context?.text || '',
    currentSelectedUnbranded: requestParams.unbranded || false,
    currentSelectedPersona: requestParams.persona || '',
    currentSelectedExtendedLibraryFlag:
      requestParams.extendedLibraryEnabled || false,
    currentSelectedSections: requestParams.sections || [],
    daring: requestParams.tone === 'daring',
    compassionate: requestParams.tone === 'compassionate',
    currentSelectedFileData: requestParams.fileData || [],
    currentSelectedModuleData: requestParams.moduleData || {},
    currentContentType: requestParams.channel,
    currentAvailableActionOptions: filtersState.actionsOptions.options,
    currentAvailableBrandOptions: filtersState.brandsOptions.options,
    currentAvailableDestinationFormatOptions:
      filtersState.destinationFormatOptions.options,
    currentAvailableIndicationOptions: filtersState.indicationsOptions.options,
    currentAvailableCountryOptions: filtersState.countriesOptions.options,
    currentAvailableAudienceOptions: filtersState.audiencesOptions.options,
    currentAvailableChannelOptions: filtersState.channelsOptions.options,
    currentAvailableContentTypeOptions: filtersState.channelsOptions.options,
    currentAvailableToneOptions: filtersState.tonesOptions.options,
    currentAvailablePrimaryMessageOptions:
      filtersState.primaryMessagesOptions.options,
    currentAvailableLanguageOptions: filtersState.languageOptions.options,
    currentAvailableTopicOptions: [
      ...filtersState.primaryMessagesOptions.options,
      ...(filtersState.primaryMessagesOptions.extendedLibraryItems || []),
    ],
    currentAvailableContentStyleOptions: contentStyleOptions,
    currentAvailableToneLevelOptions: toneLevelOptions,
  };
  if (updateParamsSegment) {
    store.set(contentParamsSegment, updatedParamsSegment);
    return { paramsUsed, retryPromptParams: updatedParamsSegment };
  }
  return paramsUsed;
};
